import React from "react";
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  List,
  ListOrdered,
} from "lucide-react";
import { cn } from "@/lib/utils";

export type FormatType =
  | "bold"
  | "italic"
  | "underline"
  | "link"
  | "bullet"
  | "number";

interface NoteFormattingToolbarProps {
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  getTextarea?: () => HTMLTextAreaElement | null;
  onTextChange?: (newText: string) => void;
  className?: string;
  size?: "sm" | "default";
}

export const applyFormatToTextarea = (
  textarea: HTMLTextAreaElement,
  type: FormatType,
  onTextChange?: (newText: string) => void,
) => {
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? 0;
  const text = textarea.value ?? "";
  const selected = text.substring(start, end);

  let replacement = "";
  let newCursorStart = start;
  let newCursorEnd = end;

  switch (type) {
    case "bold": {
      if (selected) {
        replacement = `**${selected}**`;
        newCursorStart = start;
        newCursorEnd = start + replacement.length;
      } else {
        replacement = `**negrito**`;
        newCursorStart = start + 2;
        newCursorEnd = start + 9;
      }
      break;
    }
    case "italic": {
      if (selected) {
        replacement = `*${selected}*`;
        newCursorStart = start;
        newCursorEnd = start + replacement.length;
      } else {
        replacement = `*itálico*`;
        newCursorStart = start + 1;
        newCursorEnd = start + 8;
      }
      break;
    }
    case "underline": {
      if (selected) {
        replacement = `<u>${selected}</u>`;
        newCursorStart = start;
        newCursorEnd = start + replacement.length;
      } else {
        replacement = `<u>sublinhado</u>`;
        newCursorStart = start + 3;
        newCursorEnd = start + 13;
      }
      break;
    }
    case "link": {
      const url = window.prompt(
        "Insira a URL do link (ex: https://...):",
        "https://",
      );
      if (!url || !url.trim()) return;
      const label = selected.trim() || "texto do link";
      replacement = `[${label}](${url.trim()})`;
      newCursorStart = start;
      newCursorEnd = start + replacement.length;
      break;
    }
    case "bullet": {
      if (selected && selected.includes("\n")) {
        const lines = selected.split("\n");
        replacement = lines
          .map((l) => (l.trim().startsWith("* ") ? l : `* ${l}`))
          .join("\n");
        newCursorStart = start;
        newCursorEnd = start + replacement.length;
      } else {
        const needsNewline = start > 0 && text[start - 1] !== "\n";
        const prefix = needsNewline ? "\n* " : "* ";
        replacement = selected ? `${prefix}${selected}` : `${prefix}item`;
        newCursorStart = start + replacement.length;
        newCursorEnd = newCursorStart;
      }
      break;
    }
    case "number": {
      if (selected && selected.includes("\n")) {
        const lines = selected.split("\n");
        replacement = lines
          .map((l, i) => `${i + 1}. ${l.replace(/^\d+\.\s*/, "")}`)
          .join("\n");
        newCursorStart = start;
        newCursorEnd = start + replacement.length;
      } else {
        const needsNewline = start > 0 && text[start - 1] !== "\n";
        const prefix = needsNewline ? "\n1. " : "1. ";
        replacement = selected ? `${prefix}${selected}` : `${prefix}item`;
        newCursorStart = start + replacement.length;
        newCursorEnd = newCursorStart;
      }
      break;
    }
  }

  const newText = text.substring(0, start) + replacement + text.substring(end);
  textarea.value = newText;

  // Dispara eventos para o React / React Hook Form capturar
  const inputEvent = new Event("input", { bubbles: true });
  textarea.dispatchEvent(inputEvent);

  const changeEvent = new Event("change", { bubbles: true });
  textarea.dispatchEvent(changeEvent);

  if (onTextChange) {
    onTextChange(newText);
  }

  textarea.focus();
  textarea.setSelectionRange(newCursorStart, newCursorEnd);
};

export const NoteFormattingToolbar: React.FC<NoteFormattingToolbarProps> = ({
  textareaRef,
  getTextarea,
  onTextChange,
  className = "",
  size = "sm",
}) => {
  const handleAction = (
    e: React.MouseEvent<HTMLButtonElement>,
    type: FormatType,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    let targetTextarea: HTMLTextAreaElement | null = null;
    if (textareaRef?.current) {
      targetTextarea = textareaRef.current;
    } else if (getTextarea) {
      targetTextarea = getTextarea();
    }

    if (!targetTextarea) return;

    applyFormatToTextarea(targetTextarea, type, onTextChange);
  };

  const btnClass = cn(
    "flex items-center justify-center rounded transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/80 active:bg-muted font-medium cursor-pointer border border-transparent hover:border-border/60",
    size === "sm" ? "h-6 px-1.5 text-xs" : "h-7 px-2 text-xs",
  );

  return (
    <div
      className={cn(
        "flex items-center gap-0.5 p-1 rounded-md bg-muted/40 border border-border/60 flex-wrap select-none",
        className,
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => handleAction(e, "bold")}
        className={btnClass}
        title="Negrito (**texto**)"
      >
        <Bold className="w-3.5 h-3.5 mr-1 stroke-[2.5]" />
        <span className="text-[10px] font-bold">Negrito</span>
      </button>

      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => handleAction(e, "italic")}
        className={btnClass}
        title="Itálico (*texto*)"
      >
        <Italic className="w-3.5 h-3.5 mr-1" />
        <span className="text-[10px] italic">Itálico</span>
      </button>

      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => handleAction(e, "underline")}
        className={btnClass}
        title="Sublinhado (<u>texto</u>)"
      >
        <Underline className="w-3.5 h-3.5 mr-1" />
        <span className="text-[10px] underline">Sublinhado</span>
      </button>

      <div className="w-[1px] h-3.5 bg-border/60 mx-0.5" />

      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => handleAction(e, "link")}
        className={btnClass}
        title="Inserir Link ([texto](url))"
      >
        <LinkIcon className="w-3.5 h-3.5 mr-1" />
        <span className="text-[10px]">Link</span>
      </button>

      <div className="w-[1px] h-3.5 bg-border/60 mx-0.5" />

      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => handleAction(e, "bullet")}
        className={btnClass}
        title="Lista com marcadores (* item)"
      >
        <List className="w-3.5 h-3.5 mr-1" />
        <span className="text-[10px]">Marcadores</span>
      </button>

      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => handleAction(e, "number")}
        className={btnClass}
        title="Lista numerada (1. item)"
      >
        <ListOrdered className="w-3.5 h-3.5 mr-1" />
        <span className="text-[10px]">Numerada</span>
      </button>
    </div>
  );
};
